import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ListTree, Network, Plus, type LucideIcon } from 'lucide-react';
import { clsx } from 'clsx';
import { Avatar } from '@/components/Avatar';
import type { OrgChartView } from '@/hooks/useOrgChartView';
import type MemberModel from '@/models/entities/MemberModel';
import type TeamMemberModel from '@/models/entities/TeamMemberModel';
import type { TeamComposition } from './teamOrgChart';
import { companyRoleIcon, ORG_GROUP_ICONS, teamRoleIcon } from '@/components/roles/roleIcons';
import { RoleLabel } from '@/components/roles/RoleLabel';
import { TEAM_ROLE } from '@/models/references/references';

/** La vue par défaut (arbre) en premier */
const VIEWS: { view: OrgChartView; label: string; icon: typeof ListTree }[] = [
  { view: 'tree', label: 'Arbre', icon: Network },
  { view: 'hierarchy', label: 'Hiérarchie', icon: ListTree },
];

export function OrgChartViewToggle({ view, onChange }: { view: OrgChartView; onChange: (view: OrgChartView) => void }) {
  return (
    <div role="group" aria-label="Affichage de l'organigramme" className="inline-flex rounded-lg bg-slate-100 p-1">
      {VIEWS.map(({ view: value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          aria-pressed={view === value}
          onClick={() => onChange(value)}
          className={clsx(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
            view === value ? 'bg-card text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
          )}
        >
          <Icon className="w-4 h-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

export interface OrgChartProps {
  /** En haut : Dirigeants et Assistants de la société */
  direction: MemberModel[];
  composition: TeamComposition;
  /** Équipe active : manager et équipiers modifiables ; archivée, seule la direction est affichée */
  editable: boolean;
  onAssignManager: () => void;
  onAddTeammate: () => void;
  /** Menu ⋮ d'un membre de l'équipe (manager ou équipier) */
  memberActions: (teamMember: TeamMemberModel) => ReactNode;
  /** Image à télécharger : sans menus ni cases d'ajout, « Pas de manager » à la place de la case pour en assigner un */
  forExport?: boolean;
}

/**
 * Organigramme du prototype (TeamDetail) : direction > manager > équipiers, chaque niveau décalé vers la droite et
 * relié au précédent par un trait
 */
export function HierarchyOrgChart({
  direction,
  composition: { manager, teammates },
  editable,
  onAssignManager,
  onAddTeammate,
  memberActions,
  forExport = false,
}: OrgChartProps) {
  const actionsOf = (teamMember: TeamMemberModel) => (forExport ? undefined : memberActions(teamMember));

  return (
    <TreeLevel>
      <OrgBox
        title="Direction"
        icon={ORG_GROUP_ICONS.direction}
        badgeClassName="text-emerald-700 bg-emerald-100"
        boxClassName="bg-emerald-50 border-emerald-100"
        count={direction.length}
        testId="org-direction"
      >
        {direction.length ? (
          direction.map((member) => (
            <Person
              key={member.id}
              firstName={member.firstName}
              lastName={member.lastName}
              to={`/employees/${member.id}`}
              detail={<RoleLabel icon={companyRoleIcon(member.companyRoleReference)} label={member.companyRoleLabel} />}
            />
          ))
        ) : (
          <p className="text-sm text-slate-500">Aucun Dirigeant ni Assistant.</p>
        )}
      </OrgBox>

      {editable && (
        <TreeLevel nested>
          <OrgBox
            title="Manager"
            icon={ORG_GROUP_ICONS.manager}
            badgeClassName={manager ? 'text-blue-700 bg-blue-100' : 'text-slate-500 bg-slate-100'}
            boxClassName={manager ? 'bg-blue-50 border-blue-100' : 'bg-card border-dashed border-slate-300'}
            testId="org-manager"
          >
            {manager ? (
              <Person
                firstName={manager.firstName}
                lastName={manager.lastName}
                to={`/employees/${manager.memberId}`}
                actions={actionsOf(manager)}
              />
            ) : forExport ? (
              <p className="py-1 text-sm text-slate-400 italic text-center">Pas de manager</p>
            ) : (
              <button
                onClick={onAssignManager}
                className="w-full flex items-center justify-center gap-2 py-1 text-sm text-slate-400 italic hover:text-blue-600"
              >
                <Plus className="w-3 h-3" />
                Assigner un manager
              </button>
            )}
          </OrgBox>

          <TreeLevel nested>
            <OrgBox
              title="Équipiers"
              icon={ORG_GROUP_ICONS.teammates}
              badgeClassName="text-slate-600 bg-slate-200"
              boxClassName="bg-slate-50 border-slate-200"
              count={teammates.length}
              testId="org-teammates"
            >
              {teammates.map((teammate) => (
                <Person
                  key={teammate.id}
                  firstName={teammate.firstName}
                  lastName={teammate.lastName}
                  to={`/employees/${teammate.memberId}`}
                  actions={actionsOf(teammate)}
                />
              ))}
              {forExport ? (
                !teammates.length && <p className="text-sm text-slate-400 italic">Aucun équipier.</p>
              ) : (
                <button
                  onClick={onAddTeammate}
                  className="w-full mt-2 py-2 border border-dashed border-slate-300 rounded flex items-center justify-center gap-1 text-slate-400 hover:text-slate-600 hover:border-slate-400 text-sm transition-colors"
                >
                  <Plus className="w-3 h-3" /> Ajouter
                </button>
              )}
            </OrgBox>
          </TreeLevel>
        </TreeLevel>
      )}
    </TreeLevel>
  );
}

/**
 * Organigramme classique, de haut en bas, relié par des traits : l'encadré de la direction, le manager (ou la case pour
 * en assigner un), puis l'encadré des équipiers, qui passent à la ligne au-delà de cinq (pas de défilement horizontal)
 */
export function TreeOrgChart({
  direction,
  composition: { manager, teammates },
  editable,
  onAssignManager,
  onAddTeammate,
  memberActions,
  forExport = false,
}: OrgChartProps) {
  const actionsOf = (teamMember: TeamMemberModel) => (forExport ? undefined : memberActions(teamMember));

  return (
    <div className="flex flex-col items-center py-2">
      <Group
        title="Direction"
        icon={ORG_GROUP_ICONS.direction}
        badgeClassName="text-emerald-700 bg-emerald-100"
        boxClassName="bg-emerald-50 border-emerald-100"
        testId="org-direction"
      >
        {direction.length ? (
          direction.map((member) => (
            <PersonNode
              key={member.id}
              firstName={member.firstName}
              lastName={member.lastName}
              to={`/employees/${member.id}`}
              detail={<RoleLabel icon={companyRoleIcon(member.companyRoleReference)} label={member.companyRoleLabel} />}
            />
          ))
        ) : (
          <p className="px-4 text-sm text-slate-500">Aucun Dirigeant ni Assistant.</p>
        )}
      </Group>

      {editable && (
        <>
          <VerticalLine />
          <div data-testid="org-manager">
            {manager ? (
              <PersonNode
                firstName={manager.firstName}
                lastName={manager.lastName}
                to={`/employees/${manager.memberId}`}
                detail={<RoleLabel icon={teamRoleIcon(TEAM_ROLE.MANAGER)} label="Manager" />}
                highlighted
                actions={actionsOf(manager)}
              />
            ) : forExport ? (
              <div className="w-40 min-h-[8.5rem] border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center text-sm text-slate-400 italic">
                Pas de manager
              </div>
            ) : (
              <AddNode label="Assigner un manager" onClick={onAssignManager} />
            )}
          </div>

          <VerticalLine />
          <Group
            title={`Équipiers · ${teammates.length}`}
            icon={ORG_GROUP_ICONS.teammates}
            badgeClassName="text-slate-600 bg-slate-200"
            boxClassName="bg-slate-50 border-slate-200"
            testId="org-teammates"
          >
            {teammates.map((teammate) => (
              <PersonNode
                key={teammate.id}
                firstName={teammate.firstName}
                lastName={teammate.lastName}
                to={`/employees/${teammate.memberId}`}
                actions={actionsOf(teammate)}
              />
            ))}
            {forExport ? (
              !teammates.length && <p className="px-4 text-sm text-slate-400 italic">Aucun équipier.</p>
            ) : (
              <AddNode label="Ajouter" onClick={onAddTeammate} />
            )}
          </Group>
        </>
      )}
    </div>
  );
}

/** Trait vertical entre deux niveaux de l'arbre */
function VerticalLine() {
  return <div className="w-0.5 h-6 bg-slate-300" aria-hidden />;
}

/** Encadré d'un niveau de l'arbre (direction, équipiers) : ses cases côte à côte, cinq au plus par ligne */
function Group({
  title,
  icon: Icon,
  badgeClassName,
  boxClassName,
  testId,
  children,
}: {
  title: string;
  /** Picto du groupe (ORG_GROUP_ICONS) */
  icon: LucideIcon;
  badgeClassName: string;
  boxClassName: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div className={clsx('border rounded-xl p-3 max-w-full', boxClassName)} data-testid={testId}>
      <p className="mb-3 text-center">
        <span
          className={clsx(
            'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
            badgeClassName,
          )}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden />
          {title}
        </span>
      </p>
      <div className="flex flex-wrap justify-center gap-3 max-w-[52rem]">{children}</div>
    </div>
  );
}

/** Case d'une personne dans l'arbre : pastille, NOM Prénom (lien vers sa fiche employé), précision, menu ⋮ */
function PersonNode({
  firstName,
  lastName,
  to,
  detail,
  highlighted = false,
  actions,
}: {
  firstName: string;
  lastName: string;
  to: string;
  detail?: ReactNode;
  highlighted?: boolean;
  actions?: ReactNode;
}) {
  return (
    <div
      className={clsx(
        'relative w-40 border rounded-lg px-3 pt-4 pb-3 flex flex-col items-center text-center shadow-sm',
        highlighted ? 'bg-blue-50 border-blue-200' : 'bg-card border-slate-200',
      )}
    >
      {actions && <div className="absolute top-1 right-1">{actions}</div>}
      <Avatar firstName={firstName} lastName={lastName} size="sm" className="w-9 h-9 border border-slate-200 mb-2" />
      <Link to={to} className="text-sm text-slate-900 hover:text-blue-700 hover:underline leading-tight">
        <span className="block font-semibold">{lastName}</span> <span className="block">{firstName}</span>
      </Link>
      {detail && <span className="mt-1 text-xs text-slate-500">{detail}</span>}
    </div>
  );
}

/** Case vide de l'arbre : assigner un manager, ajouter un équipier */
function AddNode({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-40 min-h-[8.5rem] border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-1 text-sm text-slate-400 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 transition-colors"
    >
      <Plus className="w-4 h-4" />
      {label}
    </button>
  );
}

/** Niveau de la vue hiérarchie : trait vertical à gauche et branche horizontale vers l'encadré */
function TreeLevel({ nested = false, children }: { nested?: boolean; children: ReactNode }) {
  return (
    <div className={clsx('relative border-l-2 border-slate-300 pb-8', nested ? 'pl-12 ml-8 mt-8' : 'pl-8 ml-4')}>
      <div className={clsx('absolute top-6 left-0 h-0.5 bg-slate-300', nested ? 'w-12' : 'w-8')} />
      {children}
    </div>
  );
}

function OrgBox({
  title,
  icon: Icon,
  badgeClassName,
  boxClassName,
  count,
  testId,
  children,
}: {
  title: string;
  /** Picto du groupe (ORG_GROUP_ICONS) */
  icon: LucideIcon;
  badgeClassName: string;
  boxClassName: string;
  count?: number;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div className={clsx('border p-4 rounded-lg min-w-[300px] max-w-md', boxClassName)} data-testid={testId}>
      <div className="flex justify-between items-center mb-3">
        <span
          className={clsx(
            'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full',
            badgeClassName,
          )}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden />
          {title}
        </span>
        {count !== undefined && (
          <span className="text-xs text-slate-500">
            {count} personne{count > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

/** Personne de la vue hiérarchie : pastille, NOM Prénom (lien vers sa fiche employé), précision et actions */
function Person({
  firstName,
  lastName,
  to,
  detail,
  actions,
}: {
  firstName: string;
  lastName: string;
  to: string;
  detail?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <Avatar firstName={firstName} lastName={lastName} size="sm" className="bg-card border border-slate-200" />
        <Link to={to} className="font-medium text-sm text-slate-900 hover:text-blue-700 hover:underline truncate">
          {lastName} {firstName}
        </Link>
        {detail && <span className="text-xs text-slate-500">{detail}</span>}
      </div>
      {actions}
    </div>
  );
}
